import { makeSelectAuthApiConfig } from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { verifyPhoneEndpoint } from 'services/user/user.config';
import { selectUserProfile } from 'services/user/user.selectors';
import { AppState, ReduxSelector } from 'store/types';
import {
  insertAccountNoteEndpoint,
  ProfileForms,
  verifyContactsEndpoint,
  verifyEmailEndpoint,
  verifyUsernameEndpoint,
} from './profile.config';
import { SubmissionOutcome } from './profile.types';

export function selectProfilePageState(reduxState: AppState) {
  return reduxState.profilePage;
}

export const selectSuggestedUsernames: ReduxSelector<string[] | null> =
  function selectSuggestedUsernames(reduxState: AppState) {
    return (
      selectProfilePageState(reduxState).status?.suggestedUsernames ?? null
    );
  };

/**
 * Generates the api config to verify a username.
 * GET '/profilo-ms/ms/profile/validate/username'
 * @param username The username to verify.
 */
export function makeSelectVerifyUsernameApiConfig(
  username: string,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectProfilePageState(reduxState: AppState) {
    const { dateOfBirth: birthDate } = selectUserProfile(reduxState);
    const requestConfig = makeSelectAuthApiConfig({
      url: verifyUsernameEndpoint,
      method: 'GET',
      params: {
        birthDate,
        username,
      },
    })(reduxState);

    return {
      resourceId: verifyUsernameEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Outputs the api config to verify if a phone number is unique.
 * GET '/profilo-ms/ms/profile/validate/phone'
 * @param phone The phone to verify (without prefix).
 */
export function makeSelectUniquePhoneApiConfig(
  phone: string,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectUniquePhoneNumber(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      url: verifyPhoneEndpoint,
      method: 'GET',
      params: { phone },
      timeout: 9000,
    })(reduxState);

    return {
      resourceId: verifyPhoneEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Outputs the api config to verify if an emai is unique.
 * GET '/profilo-ms/ms/profile/validate/email'
 * @param email The email to verify.
 */
export function makeSelectVerifyEmailApiConfig(
  email: string,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectVerfiyEmailApiConfig(reduxState: AppState) {
    const requestConfig = makeSelectAuthApiConfig({
      url: verifyEmailEndpoint,
      method: 'GET',
      params: {
        email,
      },
      timeout: 9000,
    })(reduxState);

    return {
      resourceId: verifyUsernameEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

export const selectActiveProfileForm: ReduxSelector<ProfileForms | null> =
  function selectActiveProfileForm(reduxState: AppState) {
    return selectProfilePageState(reduxState).activeForm;
  };

export const makeSelectIsProfileFormSubmitting =
  (profileFormType: ProfileForms): ReduxSelector<boolean> =>
  (reduxState: AppState) => {
    return (
      selectProfilePageState(reduxState).submittingForm === profileFormType
    );
  };

export const selectLatestSubmissionOutcome: ReduxSelector<SubmissionOutcome> = (
  reduxState: AppState,
) => selectProfilePageState(reduxState).latestSubmissionOutcome;

/**
 * Extracts the cerfication state for email and phone number retrieved from
 * GET '/profilo-ms/ms/profile/getContactCertification'.
 * @param reduxState
 */
export const selectVerifiedContactsApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: verifyContactsEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: verifyContactsEndpoint,
      method: 'GET',
    })(reduxState),
    invalidateIfPresent: true,
  };
};

export function selectInsertAccountNoteApiConfig(
  state: AppState,
): RequestApiDataIfNecessaryPayload {
  // At this moment this API is used only with those fixed data
  const data = {
    author: 'Area Riservata',
    category: 0,
    note: 'Scaricato contratto di gioco',
  };

  return {
    resourceId: insertAccountNoteEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: insertAccountNoteEndpoint,
      method: 'POST',
      data,
    })(state),
    skipDefaultServerErrorHandling: true,
  };
}
